import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { ICountry } from "@litres-libs/types";
import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IPassRecoveryMask {
  email?: string;
  phone?: string;
}

export interface ILoginState {
  authValues: {
    code: string;
    email: string;
    pwd: string;
    phoneCode: ICountry;
    phone: string;
    otpin: string;
  };
  activeStep: string;
  strategy: "email" | "phone";
  isBiblioLogin: boolean;
  isOldUser: boolean;
  isVerifyPhone: boolean;
  isLoading: boolean;
  loginFormat: string;
  passRecovery: {
    method: "email" | "phone";
    isTimerActive: boolean;
    mask?: IPassRecoveryMask;
  };
  prevSteps: string[];
  usersMerges: boolean;
  isLoginReserved: boolean;
  serverError: string | null;
}

const initialState: ILoginState = {
  authValues: {
    code: "",
    email: "",
    pwd: "",
    phoneCode: {
      id: "91",
      tel_codes: [
        {
          tel_mask: "7 XXX XXX XX XX",
          tel_code: "7",
        },
      ],
      code: "RU",
      name: "Россия",
    },
    phone: "",
    otpin: "",
  },
  activeStep: "welcome",
  strategy: "email",
  isBiblioLogin: false,
  isOldUser: false,
  isVerifyPhone: false,
  isLoading: false,
  loginFormat: "email",
  passRecovery: {
    method: "email",
    isTimerActive: false,
  },
  prevSteps: ["welcome"],
  usersMerges: false,
  isLoginReserved: false,
  serverError: null,
};

export const loginSlice = createSlice({
  name: "global/login",
  initialState,
  reducers: {
    setEmail: (state: ILoginState, action: PayloadAction<string>) => {
      state.authValues.email = action.payload;
    },
    setPhoneCode: (state: ILoginState, action: PayloadAction<ICountry>) => {
      state.authValues.phoneCode = action.payload;
    },
    setPhone: (state: ILoginState, action: PayloadAction<string>) => {
      state.authValues.phone = action.payload;
    },
    setAppCode: (state: ILoginState, action: PayloadAction<string>) => {
      state.authValues.otpin = action.payload;
    },
    setLibrary: (state: ILoginState, action: PayloadAction<string>) => {
      state.authValues.code = action.payload;
    },
    setLibraryStatus: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isBiblioLogin = action.payload;
    },
    setCurrentStep: (state: ILoginState, action: PayloadAction<string>) => {
      state.activeStep = action.payload;
    },
    setStrategy: (
      state: ILoginState,
      action: PayloadAction<"email" | "phone">
    ) => {
      state.strategy = action.payload;
    },
    setUserStatus: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isOldUser = action.payload;
    },
    setIsBiblioLogin: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isBiblioLogin = action.payload;
    },
    setIsLoading: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsOldUser: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isOldUser = action.payload;
    },
    setIsVerifyPhone: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isVerifyPhone = action.payload;
    },
    setLoginFormat: (state: ILoginState, action: PayloadAction<string>) => {
      state.loginFormat = action.payload;
    },
    setPassRecoveryMethod: (
      state: ILoginState,
      action: PayloadAction<"email" | "phone">
    ) => {
      state.passRecovery.method = action.payload;
    },
    setPassRecoveryMask: (
      state: ILoginState,
      action: PayloadAction<IPassRecoveryMask>
    ) => {
      state.passRecovery.mask = {
        ...state.passRecovery.mask,
        ...action.payload,
      };
    },
    setPassRecoveryTimer: (
      state: ILoginState,
      action: PayloadAction<boolean>
    ) => {
      state.passRecovery.isTimerActive = action.payload;
    },
    setPrevSteps: (state: ILoginState, action: PayloadAction<string[]>) => {
      state.prevSteps = action.payload;
    },
    setUsersMerges: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.usersMerges = action.payload;
    },
    setLoginReserved: (state: ILoginState, action: PayloadAction<boolean>) => {
      state.isLoginReserved = action.payload;
    },
    setServerError: (
      state: ILoginState,
      action: PayloadAction<string | null>
    ) => {
      state.serverError = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "global.login");
  },
});

export const {
  setEmail,
  setUserStatus,
  setLibraryStatus,
  setLibrary,
  setPhone,
  setPhoneCode,
  setStrategy,
  setAppCode,
  setCurrentStep,
  setIsBiblioLogin,
  setIsLoading,
  setIsOldUser,
  setIsVerifyPhone,
  setLoginFormat,
  setPassRecoveryMethod,
  setPassRecoveryMask,
  setPassRecoveryTimer,
  setPrevSteps,
  setUsersMerges,
  setLoginReserved,
  setServerError,
} = loginSlice.actions;

export default loginSlice.reducer;
